import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AlertTriangle, Clock, CheckCircle, XCircle } from "lucide-react";

const CollectionsRiskSkeleton = () => (
  <Card>
    <CardHeader>
      <CardTitle><Skeleton className="h-6 w-32" /></CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="space-y-2">
          <div className="flex justify-between">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-16" />
          </div>
          <Skeleton className="h-2 w-full" />
        </div>
      ))}
    </CardContent>
  </Card>
);

interface AgingBucket {
  label: string;
  days: string;
  amount: number;
  count: number;
  color: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  icon: React.ReactNode;
}

export function CollectionsRisk() {
  const { data: collectionsData, isLoading, error } = useQuery({
    queryKey: ["collections-risk"],
    queryFn: async () => {
      const currentDate = new Date();
      
      // Fetch all unpaid invoices with due dates
      const { data: unpaidInvoices, error: invoicesError } = await supabase
        .from('invoices')
        .select('id, amount, due_date, issue_date, client_id')
        .eq('status', 'not_paid')
        .not('due_date', 'is', null);

      if (invoicesError) {
        console.error('Error fetching unpaid invoices:', invoicesError);
        throw invoicesError;
      }

      // Initialize aging buckets
      const agingBuckets: AgingBucket[] = [
        {
          label: 'Current',
          days: 'Not overdue',
          amount: 0,
          count: 0,
          color: 'bg-green-500',
          riskLevel: 'low',
          icon: <CheckCircle className="h-4 w-4 text-green-600" />
        },
        {
          label: 'Slightly Overdue',
          days: '1-30 days',
          amount: 0,
          count: 0,
          color: 'bg-yellow-500',
          riskLevel: 'medium',
          icon: <Clock className="h-4 w-4 text-yellow-600" />
        },
        {
          label: 'Overdue',
          days: '31-60 days',
          amount: 0,
          count: 0,
          color: 'bg-orange-500',
          riskLevel: 'high',
          icon: <AlertTriangle className="h-4 w-4 text-orange-600" />
        },
        {
          label: 'Critical',
          days: '60+ days',
          amount: 0,
          count: 0,
          color: 'bg-red-500',
          riskLevel: 'critical',
          icon: <XCircle className="h-4 w-4 text-red-600" />
        }
      ];

      let totalOutstanding = 0;

      // Categorize invoices by aging
      unpaidInvoices?.forEach(invoice => {
        const dueDate = new Date(invoice.due_date);
        const daysPastDue = Math.floor((currentDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
        const amount = Number(invoice.amount);
        
        totalOutstanding += amount;

        if (daysPastDue <= 0) {
          // Not yet overdue (current)
          agingBuckets[0].amount += amount;
          agingBuckets[0].count += 1;
        } else if (daysPastDue <= 30) {
          // 1-30 days overdue (slightly overdue)
          agingBuckets[1].amount += amount;
          agingBuckets[1].count += 1;
        } else if (daysPastDue <= 60) {
          // 31-60 days overdue (overdue)
          agingBuckets[2].amount += amount;
          agingBuckets[2].count += 1;
        } else {
          // 60+ days overdue (critical)
          agingBuckets[3].amount += amount;
          agingBuckets[3].count += 1;
        }
      });

      // Calculate percentages and risk metrics
      const bucketsWithPercentages = agingBuckets.map(bucket => ({
        ...bucket,
        percentage: totalOutstanding > 0 ? (bucket.amount / totalOutstanding * 100) : 0
      }));

      // Calculate overall risk score (0-100, higher is worse)
      const riskScore = bucketsWithPercentages.reduce((score, bucket, index) => {
        const weight = index + 1; // Later buckets have higher weight
        return score + (bucket.percentage * weight * 0.25);
      }, 0);

      // Days Sales Outstanding (DSO) calculation
      const { data: recentInvoices } = await supabase
        .from('invoices')
        .select('amount, issue_date, paid_date')
        .eq('status', 'paid')
        .gte('paid_date', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()) // Last 90 days
        .limit(100);

      let totalDSO = 0;
      let dsoCount = 0;

      recentInvoices?.forEach(invoice => {
        if (invoice.paid_date && invoice.issue_date) {
          const issueDate = new Date(invoice.issue_date);
          const paidDate = new Date(invoice.paid_date);
          const daysToPayment = Math.floor((paidDate.getTime() - issueDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysToPayment >= 0) {
            totalDSO += daysToPayment;
            dsoCount += 1;
          }
        }
      });

      const avgDSO = dsoCount > 0 ? Math.round(totalDSO / dsoCount) : 30; // Default 30 days

      return {
        buckets: bucketsWithPercentages,
        totalOutstanding,
        riskScore: Math.min(100, Math.round(riskScore)),
        avgDSO,
        totalInvoices: unpaidInvoices?.length || 0
      };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  if (isLoading) {
    return <CollectionsRiskSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Collections Risk</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            Failed to load collections data
          </div>
        </CardContent>
      </Card>
    );
  }

  const data = collectionsData || {
    buckets: [],
    totalOutstanding: 0,
    riskScore: 0,
    avgDSO: 30,
    totalInvoices: 0
  };

  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'EUR',
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1
    }).format(amount);

  const getRiskColor = (score: number) => {
    if (score < 25) return 'text-green-600';
    if (score < 50) return 'text-yellow-600';
    if (score < 75) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <Card className="animate-fade-in">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Collections Risk Analysis</span>
          <div className="text-right">
            <div className={`text-sm font-medium ${getRiskColor(data.riskScore)}`}>
              Risk Score: {data.riskScore}/100
            </div>
            <div className="text-xs text-muted-foreground">
              DSO: {data.avgDSO} days
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <div className="text-sm font-medium text-muted-foreground">Total Outstanding</div>
            <div className="text-lg font-bold">{formatCurrency(data.totalOutstanding)}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-muted-foreground">Open Invoices</div>
            <div className="text-lg font-bold">{data.totalInvoices}</div>
          </div>
        </div>

        {data.buckets.map((bucket, index) => (
          <div key={bucket.label} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {bucket.icon}
                <span className="text-sm font-medium">
                  {bucket.label} ({bucket.days})
                </span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">
                  {formatCurrency(bucket.amount)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {bucket.count} invoices ({bucket.percentage.toFixed(1)}%)
                </div>
              </div>
            </div>
            <Progress 
              value={bucket.percentage} 
              className="h-2"
              // Custom color based on risk level
              style={{
                '--progress-background': bucket.riskLevel === 'low' ? 'rgb(34, 197, 94)' :
                                      bucket.riskLevel === 'medium' ? 'rgb(234, 179, 8)' :
                                      bucket.riskLevel === 'high' ? 'rgb(249, 115, 22)' :
                                      'rgb(239, 68, 68)'
              } as React.CSSProperties}
            />
          </div>
        ))}

        {data.riskScore > 50 && (
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm font-medium">
                High collection risk detected. Consider following up on overdue invoices.
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}